import { useState } from "react";
import { FormCheck, FormControl, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";

import translate from "../../../services/Translate";
import DstButton from "../../DstButton/DstButton";
import { ProxmoxDeployModalProps, ProxmoxForm } from "./ProxmoxDeployModal.d";
import { deployProxmoxUser } from "./ProxmoxDeployModal.function";

const defaultProxmoxForm: ProxmoxForm = {
  realm: "pve",
  expire: "",
  groups: "",
  enabled: true,
  comment: "",
};

const ProxmoxDeployModal = ({ show, onHide, onSuccess, user, language }: ProxmoxDeployModalProps) => {
  const [proxmoxForm, setProxmoxForm] = useState<ProxmoxForm>(defaultProxmoxForm);
  const [loading, setLoading] = useState(false);

  const _resetAndHide = () => {
    setProxmoxForm(defaultProxmoxForm);
    onHide();
  };

  const _deploy = () =>
    deployProxmoxUser(
      user.id,
      proxmoxForm.realm,
      language,
      {
        ...(proxmoxForm.expire && { expire: Math.floor(new Date(proxmoxForm.expire).getTime() / 1000) }),
        ...(proxmoxForm.groups && { groups: proxmoxForm.groups }),
        enabled: proxmoxForm.enabled,
        ...(proxmoxForm.comment && { comment: proxmoxForm.comment }),
      },
      setLoading,
      () => {
        setProxmoxForm(defaultProxmoxForm);
        onSuccess();
      }
    );

  return (
    <Modal show={show} onHide={_resetAndHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.TITLE")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label m-regular" htmlFor="proxmox-realm">
            {translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.REALM")}
          </label>
          <FormControl
            id="proxmox-realm"
            type="text"
            value={proxmoxForm.realm}
            onChange={(e) => setProxmoxForm((prev) => ({ ...prev, realm: e.target.value }))}
            placeholder="pve"
          />
        </div>
        <div className="mb-3">
          <label className="form-label m-regular" htmlFor="proxmox-expire">
            {translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.EXPIRE")}
          </label>
          <input
            type="datetime-local"
            id="proxmox-expire"
            className="form-control"
            value={proxmoxForm.expire}
            onChange={(e) => setProxmoxForm((prev) => ({ ...prev, expire: e.target.value }))}
            min={new Date().toISOString().slice(0, 16)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label m-regular d-flex align-items-center" htmlFor="proxmox-groups">
            {translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.GROUPS")}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip className="xs-regular">
                  {translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.GROUPS_TOOLTIP")}
                </Tooltip>
              }
            >
              <span className="dst-icon-info-circle ms-2" />
            </OverlayTrigger>
          </label>
          <FormControl
            id="proxmox-groups"
            type="text"
            value={proxmoxForm.groups}
            onChange={(e) => setProxmoxForm((prev) => ({ ...prev, groups: e.target.value }))}
          />
        </div>
        <div className="mb-3">
          <label className="form-label m-regular" htmlFor="proxmox-comment">
            {translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.COMMENT")}
          </label>
          <FormControl
            id="proxmox-comment"
            as="textarea"
            value={proxmoxForm.comment}
            onChange={(e) => setProxmoxForm((prev) => ({ ...prev, comment: e.target.value }))}
            placeholder={translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.COMMENT_PLACEHOLDER")}
          />
        </div>
        <FormCheck
          id="proxmox-enabled"
          label={
            <span className="m-regular">{translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.ENABLED")}</span>
          }
          checked={proxmoxForm.enabled}
          onChange={(e) => setProxmoxForm((prev) => ({ ...prev, enabled: e.target.checked }))}
        />
      </Modal.Body>
      <Modal.Footer>
        <DstButton
          variant="primary"
          value={translate(language, "PAGES.USER.USER_BLOCK.PROXMOX.MODAL.CONFIRM")}
          loading={loading}
          disabled={loading || !proxmoxForm.realm.trim()}
          clickFunction={_deploy}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ProxmoxDeployModal;
